import React from "react";
import { LineChart } from "lucide-react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import perfIcon from "../../../assets/icons/perf-icon.png";
import styles from "./GscReportViewer.module.css";
import uiStyles from "../../reportViewers/ReportUIPrimitives.module.css";
import { renderParagraphs, splitLines, clamp } from "./utils.jsx";

const sliceBetween = (source, startLabel, endLabels = []) => {
  if (!source) return "";
  const startIdx = source.indexOf(startLabel);
  if (startIdx === -1) return "";
  const afterStart = source.slice(startIdx + startLabel.length);

  if (!endLabels.length) return afterStart.trim();

  let endIdx = afterStart.length;
  endLabels.forEach((label) => {
    const i = afterStart.indexOf(label);
    if (i !== -1 && i < endIdx) endIdx = i;
  });

  return afterStart.slice(0, endIdx).trim();
};

const cleanLines = (block = "") =>
  String(block || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

const parseSummaryMetrics = (markdown = "") => {
  const detailsBlock = sliceBetween(markdown, "Details:", [
    "Top Performing Queries",
    "Top Queries",
    "Pages with High Impression",
  ]);

  const getNum = (pattern) => {
    const m = detailsBlock.match(pattern);
    if (!m) return null;
    const v = parseFloat(m[1]);
    return Number.isNaN(v) ? null : v;
  };

  return {
    totalClicks: getNum(/Total Clicks:\s*([\d.]+)/i),
    totalImpressions: getNum(/Total Impressions:\s*([\d.]+)/i),
    avgCtr: getNum(/Average CTR:\s*([\d.]+)%/i),
    avgPosition: getNum(/Average Position:\s*([\d.]+)/i),
  };
};

const parseTopPerformingQueries = (markdown = "") => {
  const block = sliceBetween(markdown, "Top Performing Queries", [
    "Queries with High Impressions but Low Clicks",
    "Queries with High Impressions and Position Above 10",
    "Pages with High Impression",
  ]);
  if (!block) return [];

  const lines = cleanLines(block);
  const items = [];

  lines.forEach((line) => {
    if (!/^\d+\.\s+/.test(line)) return;
    const m =
      line.match(
        /^\d+\.\s+(.+?)\s+—\s+Clicks\s+(\d+),\s+Impr\s+(\d+),\s+CTR\s+([\d.]+)%,\s*Pos\s+([\d.]+)/i,
      ) ||
      line.match(
        /^\d+\.\s+(.+?)\s+-\s+Clicks\s+(\d+),\s+Impr\s+(\d+),\s+CTR\s+([\d.]+)%,\s*Pos\s+([\d.]+)/i,
      );
    if (!m) return;
    items.push({
      query: m[1].trim(),
      clicks: parseFloat(m[2]),
      impressions: parseFloat(m[3]),
      ctr: parseFloat(m[4]),
      position: parseFloat(m[5]),
    });
  });

  return items;
};

const parseHighImprHighPosQueries = (markdown = "") => {
  const block = sliceBetween(
    markdown,
    "Queries with High Impressions and Position Above 10",
    ["Pages with High Impression", "Ranking Trends over time"],
  );
  if (!block) return [];

  const lines = cleanLines(block);
  const items = [];

  lines.forEach((line) => {
    if (!/^\d+\.\s+/.test(line)) return;
    const m =
      line.match(/^\d+\.\s+(.+?)\s+—\s+Impr\s+(\d+),\s+Pos\s+([\d.]+)/i) ||
      line.match(/^\d+\.\s+(.+?)\s+-\s+Impr\s+(\d+),\s+Pos\s+([\d.]+)/i);
    if (!m) return;
    items.push({
      query: m[1].trim(),
      impressions: parseFloat(m[2]),
      position: parseFloat(m[3]),
    });
  });

  return items;
};

const parseRankingTrends = (markdown = "") => {
  const block = sliceBetween(markdown, "Ranking Trends over time", [
    "Top Queries",
    "Top Pages",
  ]);
  if (!block) return [];

  const lines = cleanLines(block);
  const items = [];

  lines.forEach((line) => {
    const m = line.match(
      /Week\s+(\d+)[^:]*:\s*Clicks\s+(\d+),\s*Impr\s+(\d+),\s*CTR\s+([\d.]+)%\s*@\s*Pos\s*([\d.]+)/i,
    );
    if (!m) return;
    items.push({
      label: `Week ${m[1]}`,
      weekIndex: parseInt(m[1], 10),
      clicks: parseFloat(m[2]),
      impressions: parseFloat(m[3]),
      ctr: parseFloat(m[4]),
      position: parseFloat(m[5]),
    });
  });

  return items.sort((a, b) => a.weekIndex - b.weekIndex);
};

const parseCountries = (markdown = "") => {
  const block = sliceBetween(markdown, "Countries:", []);
  if (!block) return [];

  const lines = cleanLines(block);
  const items = [];
  lines.forEach((line) => {
    const m =
      line.match(/^[-•]\s+(.+?)\s+—\s+Users\s+(\d+)/i) ||
      line.match(/^[-•]\s+(.+?)\s*:\s*Users\s+(\d+)/i);
    if (!m) return;
    items.push({ country: m[1].trim(), users: parseFloat(m[2]) });
  });
  return items;
};

const parseNarrativeSummary = (markdown = "") => {
  const m = markdown.match(/Summary:\s*([\s\S]*?)(?:\nDetails:|\n\nDetails:)/i);
  if (!m) return [];
  return splitLines(m[1])
    .map((line) => line.trim())
    .filter(Boolean);
};

const GscReportViewer = ({ markdown }) => {
  if (!markdown) return null;

  const PLOT_H = 250;
  const MIN_BAR = 10;
  const POS_BEST = 1;
  const POS_WORST = 20;
  const AXIS_TICKS = [1, 5, 10, 15, 20];

  const summaryMetrics = parseSummaryMetrics(markdown);
  const topQueries = parseTopPerformingQueries(markdown);
  const highPosQueries = parseHighImprHighPosQueries(markdown);
  const trends = parseRankingTrends(markdown);
  const countries = parseCountries(markdown);
  const summarySentences = parseNarrativeSummary(markdown);

  const hasAnyStructured =
    summaryMetrics.totalClicks != null ||
    topQueries.length > 0 ||
    highPosQueries.length > 0 ||
    trends.length > 0 ||
    countries.length > 0;

  if (!hasAnyStructured) {
    return (
      <div className={styles.card}>{renderParagraphs(markdown, styles)}</div>
    );
  }

  const maxHighPosImpr = highPosQueries.length
    ? Math.max(...highPosQueries.map((q) => q.impressions))
    : 0;
  const maxCountryUsers = countries.length
    ? Math.max(...countries.map((c) => c.users))
    : 0;

  const clamp01 = (n) => Math.max(0, Math.min(1, n));
  const positionToHeight = (pos) => {
    if (!Number.isFinite(pos)) return MIN_BAR;
    const score = clamp01((POS_WORST - pos) / (POS_WORST - POS_BEST));
    return MIN_BAR + Math.round(score * (PLOT_H - MIN_BAR));
  };

  const bestWeek = trends.reduce((best, t) => {
    if (!best) return t;
    return (t.position ?? Infinity) < (best.position ?? Infinity) ? t : best;
  }, null);
  const topCtrLabel = bestWeek
    ? `Best Rank · ${bestWeek.label.replace("Week ", "W")} @ Pos ${bestWeek.position}`
    : null;
  const geoUrl =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
  const normalizeCountry = (value = "") => {
    const raw = String(value || "")
      .trim()
      .toLowerCase();
    const aliases = {
      usa: "united states of america",
      "u.s.a.": "united states of america",
      "u.s.": "united states of america",
      "united states": "united states of america",
      "united states of america": "united states of america",
      uk: "united kingdom",
      "u.k.": "united kingdom",
      uae: "united arab emirates",
      "u.a.e.": "united arab emirates",
    };
    return aliases[raw] || raw;
  };
  const countrySet = new Set(countries.map((c) => normalizeCountry(c.country)));

  return (
    <div className={styles.card}>
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIcon} aria-hidden="true">
            <img src={perfIcon} alt="Performance" width={18} height={18} />
          </div>
          <h4 className={styles.heroTitle}>Performance</h4>
        </div>
        <p className={styles.heroSubtitle}>
          Shopify search signals and user behaviour pulse
        </p>
      </div>

      {summaryMetrics.totalClicks != null ||
      summaryMetrics.totalImpressions != null ? (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Clicks and impressions</div>
          <div className={styles.metricsRow}>
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>
                {summaryMetrics.totalClicks != null
                  ? summaryMetrics.totalClicks.toLocaleString()
                  : "--"}
              </div>
              <div className={styles.metricLabel}>Clicks</div>
            </div>
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>
                {summaryMetrics.totalImpressions != null
                  ? summaryMetrics.totalImpressions.toLocaleString()
                  : "--"}
              </div>
              <div className={styles.metricLabel}>Impressions</div>
            </div>
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>
                {summaryMetrics.avgCtr != null
                  ? `${summaryMetrics.avgCtr}%`
                  : "--"}
              </div>
              <div className={styles.metricLabel}>Average CTR</div>
            </div>
            <div className={styles.metricItem}>
              <div className={styles.metricValue}>
                {summaryMetrics.avgPosition ?? "--"}
              </div>
              <div className={styles.metricLabel}>Average Position</div>
            </div>
          </div>
        </div>
      ) : null}

      {(trends.length > 0 || countries.length > 0) && (
        <div className={styles.overviewGrid}>
          {trends.length > 0 ? (
            <div className={styles.panel}>
              <div className={styles.panelTitle}>
                Ranking trend over four weeks
              </div>

              <div className={styles.trendWrap}>
                <div className={styles.trendAxis} style={{ height: PLOT_H }}>
                  {AXIS_TICKS.map((tick) => (
                    <span key={tick}>Pos {tick}</span>
                  ))}
                </div>

                <div className={styles.trendScroll}>
                  <div className={styles.trendPlot} style={{ height: PLOT_H }}>
                    <div className={styles.trendGridTop} />
                    <div className={styles.trendGridMid} />
                    <div className={styles.trendGridBottom} />

                    {topCtrLabel ? (
                      <div className={styles.trendCallout}>{topCtrLabel}</div>
                    ) : null}

                    <div className={styles.trendBars}>
                      {trends.map((t) => {
                        const heightPx = positionToHeight(t.position);

                        return (
                          <div key={t.label} className={styles.trendColumn}>
                            <div className={styles.trendBarTrack}>
                              <div
                                className={styles.trendBar}
                                style={{ height: heightPx }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className={styles.trendLabelsRow}>
                    {trends.map((t) => (
                      <div
                        key={`${t.label}-labels`}
                        className={styles.trendColumnLabels}
                      >
                        <div className={styles.trendWeek}>
                          {t.label.replace("Week ", "W")}
                        </div>
                        <div className={styles.trendPos}>Pos {t.position}</div>
                        <div className={styles.trendCtr}>{t.ctr}% CTR</div>
                        <div className={styles.trendMetaSmall}>
                          {t.impressions} impr
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {countries.length > 0 ? (
            <div className={styles.panel}>
              <div className={styles.mapFrame}>
                <ComposableMap projectionConfig={{ scale: 110 }}>
                  <Geographies geography={geoUrl}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const name = normalizeCountry(
                          geo.properties.name || "",
                        );
                        const active = countrySet.has(name);
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            className={styles.mapGeo}
                            style={{
                              default: {
                                fill: active ? "#45e0c1" : "#e7ebf3",
                                outline: "none",
                              },
                              hover: {
                                fill: active ? "#3bd1b3" : "#dbe1ef",
                                outline: "none",
                              },
                              pressed: {
                                fill: active ? "#2db39a" : "#cfd7ea",
                                outline: "none",
                              },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ComposableMap>
              </div>
              <div className={styles.panelTitle}>Top countries by users</div>
              <div className={styles.countryList}>
                {countries.map((c) => {
                  const ratio =
                    maxCountryUsers > 0 ? c.users / maxCountryUsers : 0;
                  return (
                    <div key={c.country} className={styles.countryRow}>
                      <span className={styles.countryLabel}>{c.country}</span>
                      <span className={styles.countryValue}>{c.users}</span>
                      <div className={styles.countryBarOuter}>
                        <div
                          className={styles.countryBarInner}
                          style={{ width: `${clamp(ratio * 100, 8, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {topQueries.length > 0 ? (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Top queries by impressions</div>
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <span>Query</span>
              <span>Impressions</span>
              <span>CTR</span>
              <span>Position</span>
            </div>
            {topQueries.map((q) => (
              <div key={q.query} className={styles.tableRow}>
                <span className={styles.queryName}>{q.query}</span>
                <span>{q.impressions}</span>
                <span>{q.ctr}%</span>
                <span>{q.position}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {highPosQueries.length > 0 ? (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            Queries needing stronger ranking
          </div>
          <div className={styles.problemList}>
            {highPosQueries.map((q) => {
              const ratio =
                maxHighPosImpr > 0 ? q.impressions / maxHighPosImpr : 0;
              return (
                <div key={q.query} className={styles.problemRow}>
                  <span className={styles.problemLabel}>{q.query}</span>
                  <div className={styles.problemBarOuter}>
                    <div
                      className={styles.problemBarInner}
                      style={{ width: `${clamp(ratio * 100, 6, 100)}%` }}
                    />
                  </div>
                  <span className={styles.problemMeta}>Pos {q.position}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {summarySentences.length > 0 ? (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Analysis</div>
          <div className={styles.summaryBody}>
            {summarySentences.map((line, idx) => (
              <div key={`sum-${idx}`} className={styles.summaryRow}>
                <span className={uiStyles.summaryCheck} aria-hidden="true" />
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GscReportViewer;
